/* eslint-disable */
import PropTypes from 'prop-types';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { memo, useMemo, useCallback } from 'react';
import DefaultCalendarToolbar from './customCalendarToolbar';
import DefaultCustomEvent from './customCalendarEvent';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './commonCalendar.scss';

const defaultLocalizer = momentLocalizer(moment);

// Set Monday as the first day of the week - memoize this configuration
const initializeMomentLocale = () => {
  moment.updateLocale('en', {
    week: { dow: 1 },
  });
};

// Initialize once
initializeMomentLocale();

const CommonCalendar = memo(({
  events,
  localizer,
  eventComponent,
  eventCalendarToolbar,
  dayPropGetter,
  formats,
  className = '',
  ...otherProps
}) => {

  // Memoize custom components to prevent recreation
  const CustomEvent = useMemo(() => 
    eventComponent || DefaultCustomEvent, 
    [eventComponent]
  );
  
  const CustomToolbar = useMemo(() => 
    eventCalendarToolbar || DefaultCalendarToolbar, 
    [eventCalendarToolbar]
  );

  // Memoize render functions to prevent recreation on every render
  const renderEvent = useCallback((props) => <CustomEvent {...props} />, [CustomEvent]);
  const renderToolbar = useCallback((props) => <CustomToolbar {...props} />, [CustomToolbar]);

  // Memoize components object to prevent recreation
  const calendarComponents = useMemo(() => ({
    event: renderEvent,
    toolbar: renderToolbar,
  }), [renderEvent, renderToolbar]);

  // Memoize localizer to prevent recreation
  const memoizedLocalizer = useMemo(() => 
    localizer || defaultLocalizer, 
    [localizer]
  );

  // Memoize className to prevent string concatenation on every render
  const containerClassName = useMemo(() => 
    `common-calendar-container ${className}`, 
    [className]
  );

  // Memoize static props that don't change
  const staticProps = useMemo(() => ({
    startAccessor: "start",
    endAccessor: "end",
    titleAccessor: "title",
    // Performance optimizations
    popup: true,
    popupOffset: 30,
    showMultiDayTimes: true,
    // Reduce layout calculations
    rtl: false,
    culture: 'en',
  }), []);

  // Combine all props to prevent object recreation
  const calendarProps = useMemo(() => ({
    localizer: memoizedLocalizer,
    events,
    components: calendarComponents,
    dayPropGetter,
    formats,
    ...staticProps,
    ...otherProps,
  }), [memoizedLocalizer, events, calendarComponents, dayPropGetter, formats, staticProps, otherProps]);

  return (
    <div className={containerClassName}>
      <Calendar {...calendarProps} />
    </div>
  );
});

// Add display name for better debugging
CommonCalendar.displayName = 'CommonCalendar';

CommonCalendar.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      start: PropTypes.instanceOf(Date),
      end: PropTypes.instanceOf(Date),
    }),
  ).isRequired,
  localizer: PropTypes.object,
  eventComponent: PropTypes.elementType,
  eventCalendarToolbar: PropTypes.elementType,
  dayPropGetter: PropTypes.func,
  formats: PropTypes.object,
  className: PropTypes.string,
};

export default CommonCalendar;